import React, { useEffect, useRef, useState } from 'react';
import './FaqSection.scss';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
  CardContent,
  Divider,
  CardMedia,
  Container
} from '@mui/material';
import useHandleAnchorClick from '../../../hooks/useHandleAnchorClick';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import customerSupport from '../../../assets/images/faqImage/customer_support.png';
import cancelIcon from '../../../assets/images/faqImage/cancel.svg';
import chevron from '../../../assets/images/faqImage/chevron.svg';
import Toggle from './Toggle';
import { useNavigate, useParams } from 'react-router-dom';
import FAQSearchBar from './FAQSearchBar';
import { useTranslation } from 'react-i18next';

const FaqSection = ({ announcementBanner, logoHeight }) => {
  const [filteredData, setFilteredData] = useState<any>([]);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [optionsHeight, setOptionsHeight] = useState(0);
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.between('xs', 'sm'));
  const isSM = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMD = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isLG = useMediaQuery(theme.breakpoints.between('lg', 'xl'));
  const isXL = useMediaQuery(theme.breakpoints.up('xl'));
  const handleAnchorClick = useHandleAnchorClick();
  const params = useParams();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();

  const faqLabels = [
    'accordion-body.all',
    'accordion-body.seller',
    'accordion-body.agency'
  ];

  const [expanded, setExpanded] = useState(null);
  const [currIndex, setCurrIndex] = useState(-1);
  const [statusType, setStatusType] = useState(t('accordion.faq.statusType'));
  const [currentAccordians, setCurrentAccordians] = useState<any>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const faqRefs = useRef<any>([]);
  const [clientWidth, setClientWidth] = useState(0);
  const [anchorEl, setAnchorEl] = useState<string>('');
  const [liveRegionMessage, setLiveRegionMessage] = useState('');

  const faqSection: any[] = t('faq_sections', { returnObjects: true, defaultValue: [] });
  const faqHeader: any = t('faq_header', { defaultValue: null });

  const dialogRef = useRef<any>([]);

  const data: any = t('accordion.faq', {returnObjects: true, defaultValue: {}})

  useEffect(() => {
    containerRef.current && setClientWidth(containerRef.current?.clientWidth);
  }, [containerRef.current?.clientWidth]);

  const handleExpandClick = (
    rowIndex: any,
    index: any,
    header: any,
    isRozie: boolean
  ) => {
    setCurrentAccordians([]);
    navigate(`/${i18n.language}/support/faq/${header}`);
    if (!isRozie && index === currIndex) {
      setExpanded(null);
      setCurrIndex(-1);
    } else {
      setExpanded(rowIndex);
      setCurrIndex(index);
    }
  };

  useEffect(()=>{
    if(expanded !== null){
     setTimeout(() => setLiveRegionMessage(`${filteredData[currIndex]?.title}-FAQ topics`), 100);
    }
    setTimeout(()=>{
      setLiveRegionMessage('')
    if(expanded !== null && dialogRef.current){
      dialogRef.current[0].focus();
    } 
  },1000);
   // eslint-disable-next-line react-hooks/exhaustive-deps
  },[expanded]);

  useEffect(() => {
    if (selectedOption) {
      filteredData.forEach((item: any, index: number) => {
        item.topics.forEach((topic: any, k: number) => {
          if (topic.summary === selectedOption) {
            const cardsPerRow = getCardsPerRow();
            const rowIndex: any = Math.floor(index / cardsPerRow);
            handleExpandClick(rowIndex, index, item.title, true);
            handleAccordionChange(k)(null, true);
            setAnchorEl(`faq-${item.title}-${k}`);
          }
        });
      });
    } else {
      setExpanded(null);
      setCurrIndex(-1);
      navigate(`/${i18n.language}/support/faq`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  useEffect(() => {
    if (
      anchorEl &&
      faqRefs.current.length &&
      filteredData.some((section) =>
        section.topics.some((topic) => topic.summary === selectedOption)
      )
    ) {
      const k = anchorEl.split('-')[2];
      const element = faqRefs.current[k];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl, faqRefs.current]);

  const handleExpandClose = (id) => {
    setExpanded(expanded === id ? null : id);
    setCurrIndex(-1);
    navigate(`/${i18n.language}/support/faq`);
  };

  const handleAccordionChange = (index: any) => (event, isExpanded) => {
    setCurrentAccordians((prevIndices: any) => {
      if (isExpanded) {
        return [...prevIndices, index];
      } else {
        return prevIndices.filter((i: any) => i !== index);
      }
    });
  };

  const handleSearchCallback = (selectedOption: string) => {
    setSelectedOption(selectedOption);
    faqRefs.current = [];
  };

  const mergeAllData = (
    dataset1: any,
    dataset2: any = [],
    dataset3: any = []
  ) => {
    const combinedData = [...dataset1, ...dataset2, ...dataset3];
    const faqMap = new Map();

    combinedData.forEach((item) => {
      if (!faqMap.has(item.header)) {
        faqMap.set(item.header, []);
      }
      faqMap.get(item.header).push(...item.FAQ);
    });

    // Ensure IDs are sequential
    faqMap.forEach((faqs, header) => {
      faqs.forEach((faq, index) => {
        faq.id = index + 1;
      });
    });

    setFilteredData(faqSection);
  };

  const getFilteredData = (label: any) => {
    const faqData = t(`accordion.faq.${faqLabels[0]}`, { returnObjects: true });
    if (label === faqLabels[0]) {
      mergeAllData(
        faqData,
        t(`accordion.faq.${faqLabels[1]}`, { returnObjects: true }),
        t(`accordion.faq.${faqLabels[2]}`, { returnObjects: true })
      );
      return;
    }

    const otherFaqData = t(`accordion.faq.${label}`, { returnObjects: true });
    mergeAllData(faqData, otherFaqData);
  };

  useEffect(() => {
    if (!filteredData.length) {
      // const json = data?.['accordion.faq'];
      // setStatusType(json.toggle[0]);
      getFilteredData(faqLabels[0]);
      if (params.type) {
        filteredData.forEach((element: any, index: any) => {
          if (element.header === params.type) {
            const cardsPerRow = getCardsPerRow();
            const rowIndex: any = Math.floor(index / cardsPerRow);
            setCurrIndex(index);
            setExpanded(rowIndex);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData]);

  const handleToggle = (selection: any) => {
    const toggleOptions = t('accordion.faq.toggle', { returnObjects: true });
    const toggleOptionsArray = Array.isArray(toggleOptions) ? toggleOptions : Object.values(toggleOptions);

    const index = toggleOptionsArray.indexOf(selection);
    const statusType = faqLabels[index];
    const filtered = filterData(statusType);
    setFilteredData(filtered);
    setStatusType(selection);
    setExpanded(null);
    setCurrIndex(-1);
  };

  const filterData = (statusType: string) => {
    if (statusType === 'accordion-body.all') {
      return faqSection.filter((section) => section.topics.length > 0);
    }
    const audienceType =
      statusType === 'accordion-body.seller' ? 'SELLER' : 'TRAVEL AGENCY';
    return faqSection
      .map((section) => {
        return {
          ...section,
          topics: section.topics.filter((topic) => {
            if (Array.isArray(topic.audience)) {
              return topic.audience.some((aud) => aud.value === audienceType);
            }
            return topic.audience === audienceType;
          })
        };
      })
      .filter((section) => section.topics.length > 0);
  };

  const getCardsPerRow = () => {
    if (isXS) return 1;
    if (isSM) return 2;
    if (isMD) return 3;
    if (isLG) return 4;
    return 4;
  };

  const faq: { imgTitle: string; imgSubtitle: string; title: string;[key: string]: any } = t('accordion.faq', { returnObjects: true });

  const cardsPerRow = getCardsPerRow();

  const addStyleToElement = (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    const elements = doc.querySelectorAll('h1');

    elements.forEach((element) => {
      element.style.fontSize = '2.4rem';
      element.style.fontWeight = '600';
      element.style.lineHeight = '27px';
    });

    return doc.body.innerHTML;
  };

  return (
    <Box
      sx={{
        mb: '48px',
        marginTop: `${logoHeight - (announcementBanner ? 79 : 62)}px`
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',

          height: '275px',
          backgroundImage:
            'linear-gradient(to right, #fbfeff, #cadde8, #B1CBDA)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <Box
          sx={{
            ...(isXL && {
              width: `${clientWidth}px`,
              margin: '0 auto',
              position: 'relative'
            })
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '40px',
              ml: isLG || isMD ? '8rem' : isXS ? '15px' : '28px',
              mr: isXS ? '2rem' : 'None',
              zIndex: 2
            }}
          >
            {/* <Typography
          height: '275px',
          backgroundImage:
            'linear-gradient(to right, #fbfeff, #cadde8, #B1CBDA)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <Box
          sx={{
            ...(isXL && {
              width: `${clientWidth}px`,
              margin: '0 auto',
              position: 'relative'
            })
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '40px',
              ml: isLG || isMD ? '8rem' : isXS ? '15px' : '28px',
              mr: isXS ? '2rem' : 'None',
              zIndex: 2
            }}
          >
            {/* <Typography
                sx={{
                  lineHeight: isXS ? '36px' : '50px',
                  width: isSM ? '70%' : 'fit-content',
                  fontSize: isXS ? '28px' : '36px',
                  fontWeight: 700,
                  color: '#000',
                  marginBottom: isXS ? '20px' : '9px'
                }}
              >
                {data.faq_header_title}
              </Typography> */}
            <Typography
              sx={{
                width: isSM ? '70%' : isMD ? '680px' : 'fit-content',
                fontSize: isXS ? '14px' : '16px',
                lineHeight: isXS ? '20px' : '25px',
                fontWeight: 400
              }}
              dangerouslySetInnerHTML={{
                __html: addStyleToElement(faqHeader)
              }}
            />
            <Box
              sx={{
                width: isSM ? '70%' : isMD ? '620px' : '100%',
                // width: '100%',
                lineHeight: isXS ? '36px' : '50px',
                fontSize: isXS ? '28px' : '36px',
                fontWeight: 700,
                color: '#000',
                marginBottom: isXS ? '20px' : '9px',
                zIndex: 1,
                position: 'relative'
              }}
            >
              <FAQSearchBar
                fabLabel={faq}
                handleSearchCallback={handleSearchCallback}
                setOptionsHeight={setOptionsHeight}
                optionsHeight={optionsHeight}
              />
            </Box>
          </Box>
          <img
            src={customerSupport}
            alt="customer support"
            style={{
              position: 'absolute',
              display: isXS ? 'none' : 'unset',
              height: '275px',
              right: isXL ? '0' : '-180px'
            }}
          />
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ p: '0 !important' }} component="main">
        <Box
          sx={{
            mt: `${optionsHeight ? optionsHeight - 10 : optionsHeight}px`,
            zIndex: 2,
            px: isXL || isLG || isMD ? '8rem' : isXS ? '15px' : '28px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: isXS ? 'column' : 'row',
              justifyContent: 'space-between',
              mb: isXS ? '32px' : '48px',
              mt: '48px',
              rowGap: '34px'
            }}
            ref={containerRef}
          >
            {/* <Typography
                sx={{
                  fontSize: isXS ? '20px' : '24px',
                  fontWeight: 700,
                  lineHeight: isXS ? '28px' : '32px',
                  color: '#000'
                }}
                variant="h1"
              >
                {faq.title}
              </Typography> */}
            <Box>
              <Toggle
                onHandle={handleToggle}
                statusType={statusType}
                data={data}
              />
            </Box>
          </Box>
          {/* {faqList.map((faqSubList,i) =>)} */}

          <Grid
            container
            spacing={'2rem'}
            rowSpacing={'3rem'}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'stretch'
            }}
          >
            {filteredData.map((item: any, index: any) => {
              const rowIndex = Math.floor(index / cardsPerRow);
              return (
                <>
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <Card
                      sx={{
                        height: '275px',
                        borderRadius: '1rem',
                        boxShadow: '0 0 1rem #ccc',
                        cursor: 'pointer',
                        position: 'relative',
                        zIndex: 2,
                        transition: 'transform 0.3s',
                        pl: '24px',
                        '&:hover': {
                          boxShadow:
                            '0 0 1rem #ccc, -4px 4px 8px rgba(0, 0, 0, 0.1), 4px -4px 8px rgba(0, 0, 0, 0.1)'
                        },
                        border:
                          index === currIndex ? '1px solid #68B1F7' : 'unset',
                        rowGap: '3rem'
                      }}
                      onClick={() =>
                        handleExpandClick(rowIndex, index, item.title, false)
                      }
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '200px',
                          justifyContent: 'space-between'
                        }}
                      >
                        <CardMedia
                          component="img"
                          src={data?.['all_images'][item.title]}
                          sx={{
                            objectFit: 'contain',
                            width: 'fit-content',
                            mt: '32px'
                          }}
                        />
                        <CardContent
                          sx={{ p: '0 !important', ml: '.5rem', mr: '2rem' }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight={'700'}
                            fontSize={'20px'}
                            lineHeight={'28px'}
                            component="div"
                            color={'#000000CC'}
                          >
                            {/* {item.header} */}
                            {item.title}
                          </Typography>
                        </CardContent>
                      </Box>
                      <Divider
                        variant="fullWidth"
                        sx={{ mt: '16px', mr: '2rem' }}
                      />
                      <Box
                        role="button"
                        aria-haspopup="dialog"
                        aria-label={`${item.title} ${t(
                          'accordion.faq.allTopics'
                        )} ${item?.topics?.length}`}
                        sx={{
                          p: 0,
                          display: 'flex',
                          alignItems: 'center',
                          ml: '.5rem',
                          mt: '16px'
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#1460AA',
                            fontWeight: '600',
                            fontSize: '16px',
                            lineHeight: '20px'
                          }}
                        >
                          {t('accordion.faq.allTopics')} ({item?.topics?.length}
                          )
                        </Typography>
                        <img
                          src={chevron}
                          alt="chevron icon"
                          aria-hidden="true"
                          style={{
                            rotate: index === currIndex ? '180deg' : 'none',
                            marginLeft: '12px',
                            width: '12px'
                          }}
                        />
                      </Box>
                    </Card>
                  </Grid>

                  {(index % cardsPerRow === cardsPerRow - 1 ||
                    index === filteredData.length - 1) &&
                    expanded === rowIndex ? (
                    <Grid item xs={12}>
                      <Collapse
                        in={expanded === rowIndex}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ mt: 2 }}
                        // tabIndex={0}
                          role="dialog"
                        //   aria-label={`${filteredData[currIndex]?.title}-FAQ topics`}
                          >
                          <Card
                           
                            elevation={0}
                           
                            aria-modal="true"
                            sx={{
                              mt: '-120px',
                              pb: '48px',
                              px: '8rem',
                              mx: '-8rem',
                              backgroundColor: '#F1F7FA',
                              transition: 'all 0.3s',
                              zIndex: 1
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                pt: '88px',
                                pb: '24px',
                                alignItems: 'center'
                              }}
                            >
                              <div
                              role="dialog"
            aria-live="polite"
            // role="status"
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              margin: '-1px',
              border: 0,
              padding: 0,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              clipPath: 'inset(50%)',
              whiteSpace: 'nowrap'
            }}
          >
            {liveRegionMessage}
          </div>
                              <Typography
                                sx={{
                                  fontSize: '28px',
                                  fontWeight: 700,
                                  lineHeight: '36px',
                                  color: '#000000CC'
                                }} 
                              >
                                {' '}
                                {filteredData[currIndex]?.title}
                              </Typography>

                                <button
                                  style={{
                                    cursor: 'pointer',
                                    marginRight: '38px',
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    width: '21px',
                                    height: '21px',
                                  }}
                                  aria-label="Cancel"
                                  onClick={handleExpandClose}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleExpandClose(currIndex);
                                    }
                                  }}
                                >
                                  <img
                                    src={cancelIcon}
                                    alt="Close dialog"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                    }}
                                  />
                                </button>
                            </Box>
                            {/* {filteredData[currIndex]?.['FAQ']?.map( */}
                            {filteredData[currIndex]?.['topics']?.map(
                              (i, k: any) => (
                              <div
                                  ref={(element) =>
                                    (faqRefs.current[k] = element)
                                  }
                                  //tabIndex={0}
                              >
                                <Accordion
                                  onChange={handleAccordionChange(k)}
                                    key={k}
                                  expanded={currentAccordians.includes(k)}
                                  elevation={0}
                                  sx={{
                                    '&::before': {
                                        content: 'none'
                                      }
                                  }}
                                  disableGutters
                                >
                                  <AccordionSummary
                                   ref={(element) =>
                                    (dialogRef.current[k] = element)
                                  }
                                    expandIcon={
                                        <img src={chevron} aria-hidden="true" alt="chevron icon" />
                                    }
                                    sx={{
                                      ...(k !== 0 && {
                                        borderTop: '1px solid',
                                          borderTopColor: 'divider'
                                      }),
                                      pr: 0,
                                        '.MuiAccordionSummary-expandIconWrapper':
                                          {
                                            marginRight: '40px'
                                      },
                                      '.MuiAccordionSummary-content': {
                                        my: '16px',
                                          ml: 0
                                        }
                                    }}
                                      aria-controls={`panel-${item[
                                        'title'
                                      ].replaceAll(' ', '')}-${k}`}
                                      id={`panel-${item['title'].replaceAll(
                                        ' ',
                                        ''
                                      )}-${k}`}
                                  >
                                    <Typography
                                      variant='h3'
                                      sx={{
                                          fontSize: currentAccordians.includes(
                                            k
                                          )
                                          ? '16px'
                                          : '14px',
                                          fontWeight:
                                            currentAccordians.includes(k)
                                          ? 700
                                          : 600,
                                          lineHeight:
                                            currentAccordians.includes(k)
                                          ? '20px'
                                              : '22px'
                                      }}
                                    >
                                        {/* {i.title} */}
                                      {i.summary}
                                    </Typography>
                                  </AccordionSummary>
                                  <AccordionDetails
                                    sx={{
                                      ...(currentAccordians.includes(k) && {
                                        borderBottom: '1px solid',
                                          borderBottomColor: '#1460AA'
                                      }),
                                      p: 0,
                                      pb: '32px',
                                        pr: '88px'
                                    }}
                                  >
                                    <Typography
                                      mx={'1.1875rem'}
                                      fontSize={'14px'}
                                      fontWeight={400}
                                      lineHeight={'20px'}
                                        // dangerouslySetInnerHTML={{
                                        //   __html: i.content
                                        // }}
                                      dangerouslySetInnerHTML={{
                                          __html: i.details
                                      }}
                                      onClick={handleAnchorClick}
                                    />
                                  </AccordionDetails>
                                </Accordion>
                              </div>
                              )
                            )}
                          </Card>
                        </Box>
                      
                      </Collapse>
                    </Grid>
                  ) : null}
                </>
              );
            })}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default FaqSection;



